import type { Intake } from '@lenderhound/shared';
import type { PowChallenge } from './pow.ts';

export interface User {
  /** `username` is null for a broker who signed in by phone - they never had one. */
  id: string; username: string | null; displayName: string | null;
  /** Power over the corpus - the admin console. */
  isAdmin: boolean;
  /**
   * The full view: the sweep, the coverage line, the retrieved sections, and the source
   * page behind a citation. Separate from isAdmin because they are separate powers; the
   * server decides both and this is only what it said. `accountLevel` in
   * @lenderhound/shared turns it into the level the components read.
   */
  isSuperuser: boolean;
}

async function json<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    // Session lives in an httpOnly cookie; without this every request is anonymous.
    credentials: 'same-origin',
    headers: init?.body ? { 'content-type': 'application/json' } : {},
    ...init,
  });
  const body = await res.json().catch(() => ({})) as { error?: string; message?: string };
  // `message` carried through for the same reason search() carries it: when the server
  // writes a sentence for the person reading it, that beats any code the UI can map.
  if (!res.ok) throw new ApiError(res.status, body.error ?? 'request_failed', body.message);
  return body as T;
}

export class ApiError extends Error {
  /**
   * `detail` is a sentence the server wrote for the person reading it, carried
   * separately from the machine-readable code.
   *
   * Most refusals are a code the UI knows how to phrase. The daily search ceiling is
   * not: only the server knows how many searches the account gets and when the count
   * resets, and "daily_limit_reached" rendered into the red banner - which is what the
   * unmapped-code fallback does - tells a broker neither.
   */
  constructor(readonly status: number, readonly code: string, readonly detail?: string) {
    super(code);
    this.name = 'ApiError';
  }
}

export const login = (username: string, password: string) =>
  json<{ user: User }>('/api/auth/login', {
    method: 'POST', body: JSON.stringify({ username, password }),
  });

/**
 * Ask for a code by text. The server answers `{ status: 'sent' }` whether it sent one or
 * not - see apps/api/src/routes/verify.ts - so a resolved promise means "if this number can
 * get a code, it is on its way", and the screen must word it that way.
 */
export const startPhoneVerification = (name: string, email: string, phone: string) =>
  json<{ status: 'sent' }>('/api/auth/phone/start', {
    method: 'POST', body: JSON.stringify({ name, email, phone }),
  });

/**
 * Trade the code for the ordinary session cookie.
 *
 * The name and email go WITH the code rather than with the request for one. Whoever types
 * the code is the only person who has shown the phone is theirs, so theirs are the details
 * the account keeps - see the comment on checkSchema in apps/api/src/routes/verify.ts.
 */
export const checkPhoneCode = (phone: string, code: string, name: string, email: string) =>
  json<{ user: User }>('/api/auth/phone/check', {
    method: 'POST', body: JSON.stringify({ phone, code, name, email }),
  });

export const logout = () => json<unknown>('/api/auth/logout', { method: 'POST' });
export const me = () => json<{ user: User | null }>('/api/auth/me');

/**
 * Whether an ingest is running, and whose lenders are moving while it does.
 *
 * See apps/api/src/maintenance.ts for why this is derived from `document_version.status`
 * rather than a flag somebody sets - and, more importantly, has to remember to clear.
 */
export interface MaintenanceStatus {
  updating: boolean;
  lenders: string[];
  /** ISO, when the oldest in-flight version was claimed. Elapsed is honest; an ETA is not. */
  startedAt: string | null;
}

export const maintenanceStatus = () => json<MaintenanceStatus>('/api/status');

/* ------------------------------------------------------------------ search */

export interface Reference {
  id: string; page_index: number; printed_label: string | null;
  section_number: string | null; section_title: string | null;
  quote: string; bbox_norm: number[] | null;
  document_title: string; document_kind: string; lender_name: string;
  /** Null when the document never stated an effective date; the badge stays silent. */
  effective_date?: string | null; age_days?: number | null;
}

/**
 * One retrieved guideline section, as the `sections` event names it.
 *
 * Named rather than left inline because the UI shows these during the ninety-second
 * synthesis wait — they are the only concrete evidence, before the answer exists, that
 * the machine is reading real documents belonging to real lenders. Superuser view only:
 * the list is a roster of which lenders are being read, which is our accounting rather
 * than something a broker acts on.
 */
export interface SectionTitle { citationId: number; lenderName: string; path: string }

export type SearchEvent =
  | { type: 'inquiry';    inquiryId: string; ltv: number }
  | { type: 'verdicts';   lenders: LenderGroup[]; coverage: Coverage }
  | { type: 'sections';   count: number; titles: SectionTitle[] }
  | { type: 'answer';     answer: Answer; retries: number; droppedFindings: number;
      /**
       * What the answer was built out of. 'matrix_only' means no guideline text was read
       * at all - the search ran on the grids and the fine print beside them, because the
       * broker described nothing unusual. A narrower claim, and it has to render as one.
       */
      basis?: 'matrix_only' | 'matrix_and_guidelines';
      /**
       * Lenders the search could not finish checking, because something on our side
       * failed - not because they said no.
       *
       * A SIDECAR beside the answer, like `coverage` beside the verdicts, and for the
       * same reason: nothing here was written by the model. It must never be folded into
       * `openQuestions`, which is business content about the loan - a transport error
       * has no business in a list a broker reads as underwriting.
       *
       * Optional on the wire so a client reading an older stream, or a replayed frame
       * stored before this existed, does not break. Absent reads as a clean sweep.
       */
      systemFailures?: { lenderSlug: string; lenderName: string }[];
      expertReview?: { offered: boolean; message: string | null } }
  | { type: 'references'; references: Reference[]; degraded?: boolean }
  | { type: 'done' }
  | { type: 'error';      message: string };

export interface Coverage {
  lendersEvaluated: number; channelsEvaluated: number; eligible: number; nearMisses: number;
  noDataFor: string[];
  /**
   * The STRICT SUBSET of noDataFor where we genuinely hold nothing for that lender.
   *
   * The two are not the same question and conflating them is what produced the
   * complaint that started this: "I provided like 8 Arc matrices... why is it saying we
   * hold no matrix?" We held twelve. The loan was simply above every band they print.
   * `noDataFor` answers "did this file find a cell", which is a question about the LOAN;
   * only this field answers "do we hold documents", which is a question about US.
   *
   * Optional because a server that predates it sends neither, and a `?? []` there must
   * read as "we cannot claim a document gap" rather than as "there are none".
   */
  noMatrixFor?: string[];
  /**
   * Form fields the broker left blank, which the sweep widened on. Optional on the wire
   * so a client reading an older stream does not break; absent reads as none.
   */
  widenedOn?: string[];
  staleDocuments: { lenderSlug: string; documentTitle: string; effectiveDate: string | null; ageDays: number }[];
}
export interface Channel {
  /** Present in the payload all along; declared now so findings can be named. */
  lenderId: string;
  /**
   * The program row this channel was read off. On every MatrixVerdict since the evaluator
   * was written, and declared now because "View Matrix" asks for a program's pages by it -
   * the program's NAME is not a key, and two lenders both sell something called "DSCR".
   */
  programId: string;
  /**
   * House reading order, 1 first and 100 the unranked default - migration 019, and the
   * whole roster ranked in 051. Present on every MatrixVerdict and therefore on the wire
   * all along; declared now because `applyResultSlots` requires it, and this frame is
   * the only place the client can learn it. A finding carries a lenderId and nothing
   * else, so rank reaches the slot rule by way of this roster or not at all.
   */
  stackRank: number;
  lenderName: string; programCode: string; programName: string;
  /**
   * The investor slug ('base' when retained). Declared alongside investorName because
   * the only handle a finding gives us on its channel is a sentence the model wrote, and
   * matching that sentence has to be allowed to try both spellings - see channelsFor in
   * Results.tsx.
   */
  investorSlug: string;
  investorName: string; isBaseChannel: boolean;
  verdict: 'eligible' | 'eligible_with_conditions' | 'ineligible' | 'no_data';
  maxLtv: number | null; requestedLtv: number; ltvHeadroom: number | null;
  maxLoanAtMaxLtv: number | null; isStale: boolean;
  blockers: { kind: string; message: string; overlayFrom: string | null; fixHint: string | null }[];
  citationIds: number[];
}

/**
 * The desk's account executive at a lender: `lender.ae_contact`, shaped by migration 053.
 *
 * Optional on the wire, and that is not defensive typing - the verdicts frame does not
 * carry it yet (the server side is being written separately), and thirteen of seventeen
 * lenders hold `{}` even once it does. Absent and empty must therefore both render as
 * nothing at all rather than as a button that reveals a blank card; 053's CHECK
 * guarantees that anything non-empty has at least a name and an email, which is what
 * makes "has a name and an email" a safe test for "has a contact".
 */
export interface AeContact { name: string; email: string; phone?: string | null }

export interface LenderGroup {
  lenderSlug: string; lenderName: string; channels: Channel[]; channelsDisagree: boolean;
  /** The lender's rank, repeated at group level by groupByLender. */
  stackRank?: number;
  aeContact?: AeContact | null;
}
export interface Cited { text: string; citationIds: number[] }
export interface Answer {
  summary: string;
  findings: {
    lenderId: string; deliveryChannel: string;
    verdict: 'yes' | 'yes_with_conditions' | 'no' | 'unknown';
    headline: string;
    conditions: Cited[];
    blockers: (Cited & { overlayFrom: string | null; fixHint: string | null })[];
    carveOutsApplied: (Cited & { satisfiedBy: string })[];
    citationIds: number[];
  }[];
  nearMisses: { lenderId: string; missBy: string; whatWouldFixIt: string; citationIds: number[] }[];
  openQuestions: string[];
}

/**
 * Stream a search.
 *
 * Hand-parsed rather than using EventSource, which cannot issue a POST and so cannot
 * carry the intake at all. The parser splits on a blank line because an SSE frame is
 * delimited that way and a chunk boundary can land anywhere - including mid-JSON, which
 * is common here since the answer frame is large.
 */
export async function search(
  intake: Intake,
  onEvent: (e: SearchEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch('/api/search', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(intake),
    ...(signal ? { signal } : {}),
  });

  if (!res.ok || !res.body) {
    const body = await res.json().catch(() => ({})) as { error?: string; message?: string };
    throw new ApiError(res.status, body.error ?? 'search_failed', body.message);
  }

  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += value;
    let split: number;
    while ((split = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, split);
      buffer = buffer.slice(split + 2);
      let event = 'message';
      let data = '';
      for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) data += line.slice(5).trim();
      }
      if (!data) continue;
      try {
        onEvent({ type: event, ...JSON.parse(data) } as SearchEvent);
      } catch {
        // A malformed frame must not tear down a stream whose remaining frames are
        // fine - the answer frame arriving late is worth more than strictness here.
        onEvent({ type: 'error', message: 'A response fragment could not be read.' });
      }
    }
  }
}

/* --------------------------------------------- the matrix behind a citation */

/**
 * The grid a cited cell sits in, and the prose printed beside it.
 *
 * Henry, 2026-09-01: "for the snippets... can we actually show the whole matrix that
 * applies?" and "I think we show both the guideline paragraph + the whole matrix".
 *
 * WHAT "THE WHOLE MATRIX THAT APPLIES" MEANS HERE, and why it is not the other three
 * things it could have meant.
 *
 * A cited cell is one number pinned by seven coordinates: program, investor, doc type,
 * occupancy, purpose, property type, and - inside the printed table - a FICO rung and a
 * loan band, sometimes split again by a DSCR floor. Six of those seven are what the
 * broker's file IS; the last two are the axes the lender actually draws a table on. So
 * the slice is every cell sharing the first six, laid out as FICO down the side and loan
 * band across the top. That is the sub-table the cited number is printed in, and it is
 * the answer to the question a broker asks the instant they see one LTV: "and if the
 * score is twenty points lower, or the loan is half a million bigger?"
 *
 * Not the whole document. A matrix PDF holds purchase, rate/term and cash-out for every
 * doc type and occupancy the lender writes - hundreds of rows, of which all but one
 * table describe a different loan than the one on screen. "Applies" is doing work in
 * Henry's sentence and this is what it excludes.
 *
 * Not the page as an image. That is `page.png`, it is superuser-only for good reason,
 * and a picture of a table cannot be scanned, tabbed through, read aloud, or told apart
 * from a screenshot somebody pasted in. Rendering our own extraction is also the more
 * honest claim: what the page shows is what the engine actually reasoned over, so a
 * mis-read row is visible here rather than hidden behind a faithful photograph of a
 * document we read wrong.
 *
 * Not one cell blown up. A single number with no neighbours is what the citation chip
 * already gives; there would be nothing new to be impressed by.
 *
 * Slices are small - across all 33,776 stored cells the average slice is 11 cells and
 * the largest is 60 - so this is a table, not a viewport onto one.
 */
export interface MatrixCell {
  ficoMin: number; ficoMax: number;
  /** Half-open on the low side: (min, max]. See matrix_cell.amt_band in migration 003. */
  loanMin: number; loanMax: number;
  /**
   * NULL is NOT missing data. A present row with no LTV is the em-dash the lender
   * printed, which means explicitly not offered; a cell with no row at all is absent
   * from this array entirely and means we have nothing. Migration 003 makes the same
   * point at length, and the grid renders the two differently because conflating them
   * reports a gap in our extraction as a decline by the lender.
   */
  maxLtv: number | null;
  maxCltv: number | null;
  minReservesMonths: number | null;
  maxDti: number | null;
  /** The citation this cell was extracted from - every cell has one. */
  citationId: string;
  /** True for the one cell the chip was clicked on. */
  isCited: boolean;
}

/**
 * One printed sub-table. DSCR is a third axis at JLD, Verus and Arc, who publish
 * separate grids at and below a 1.00 floor, so a slice can hold more than one.
 */
export interface MatrixGrid {
  /** The DSCR floor this sub-grid is drawn for; null when the program has no DSCR axis. */
  dscrMin: number | null;
  cells: MatrixCell[];
}

/** Verbatim prose printed on the same page of the same matrix as the cited grid. */
export interface MatrixProse {
  id: string;
  title: string;
  sectionNumber: string | null;
  pageStart: number;
  pageEnd: number;
  rulePolarity: 'permits' | 'restricts' | 'ineligible' | 'informational' | null;
  hasCarveouts: boolean;
  /**
   * The lender's own words, whole. NULL for an account without source-document
   * access - see the boundary argument in MatrixPanel.tsx. `bodyChars` is sent either
   * way so the panel can say how much is being withheld rather than pretending the
   * section is empty.
   */
  bodyMd: string | null;
  bodyChars: number;
}

export interface MatrixSlice {
  citationId: string;
  /** Null when this citation is not a matrix cell - a guideline quote has no grid. */
  slice: {
    lenderName: string;
    documentTitle: string;
    programName: string; programCode: string;
    investorName: string; isBaseChannel: boolean;
    docTypeCode: string; occupancy: string; purpose: string; propertyType: string;
    /**
     * The last two of the nine coordinates the slice holds fixed.
     *
     * Optional because a server older than this field sends neither, and a caption that
     * invents "standard" for a grid it cannot see is the failure this exists to prevent.
     * `'*'` means the grid is not split on that axis, which is the common case and reads
     * as nothing worth captioning; anything else is a real sub-table the broker is
     * looking at and the caption must name.
     */
    creditTier?: string; borrowerStatus?: string;
    grids: MatrixGrid[];
  } | null;
  /** Document order. Empty is normal: a third of matrix pages carry no prose at all. */
  sections: MatrixProse[];
  /** Whether `bodyMd` was stripped, stated rather than inferred from a null. */
  proseWithheld: boolean;
}

/**
 * THE ROUTE DOES NOT EXIST YET.
 *
 * `GET /api/citation/:id/matrix` is specified here and owned by apps/api, which is
 * another agent's tree. Until it lands this returns null on the 404 and the panel simply
 * does not render - the same fire-and-forget shape `recordAeContactClick` uses above,
 * and for the same reason: a citation panel that already shows the lender, the document,
 * the page and the quote must not degrade into an error box because an enhancement is
 * not deployed.
 *
 * What the server must do, exactly:
 *
 *   preHandler: requireUser  (NOT requireSourceDocumentAccess - see below)
 *
 *   -- the slice: every cell sharing the cited cell's six fixed coordinates
 *   WITH cited AS (SELECT * FROM matrix_cell WHERE citation_id = $1)
 *   SELECT mc.fico_min, mc.fico_max, mc.loan_amount_min, mc.loan_amount_max,
 *          mc.dscr_min, mc.max_ltv, mc.max_cltv, mc.min_reserves_months, mc.max_dti,
 *          mc.citation_id, (mc.citation_id = $1) AS is_cited
 *     FROM matrix_cell mc, cited
 *    WHERE mc.program_id     = cited.program_id
 *      AND mc.investor_id    = cited.investor_id
 *      AND mc.doc_type_code  = cited.doc_type_code
 *      AND mc.occupancy      = cited.occupancy
 *      AND mc.purpose        = cited.purpose
 *      AND mc.property_type  = cited.property_type
 *    ORDER BY mc.dscr_tier DESC, mc.fico_min DESC, mc.loan_amount_max ASC
 *
 *   -- the prose: sections of the SAME version whose page range covers the cited page.
 *   -- Matrix sections are flat and page-scoped (depth 1, no section_number), so the
 *   -- page IS the join. Tonight's ingest wrote 2,216 of them; before tonight this half
 *   -- of the feature was not possible at all.
 *   SELECT gs.id, gs.title, gs.section_number, gs.page_start, gs.page_end,
 *          gs.rule_polarity, gs.has_carveouts, gs.body_md
 *     FROM guideline_section gs, citation c
 *    WHERE c.id = $1
 *      AND gs.document_version_id = c.document_version_id
 *      AND c.page_index BETWEEN gs.page_start AND gs.page_end
 *    ORDER BY gs.ord
 *
 * The one thing the server must NOT copy from this client: the withholding. `bodyMd`
 * has to be nulled server-side for a caller `canOpenSourceDocuments` says no to, exactly
 * as `page.png` is refused server-side. A client that merely declines to draw the text
 * is the same product with a smaller button, which is the argument accounts.ts already
 * makes and the reason this route takes `requireUser` and then strips, rather than
 * refusing the whole payload to brokers - the cells are ours to show and the lender's
 * paragraphs are not.
 */
export async function fetchMatrixSlice(
  citationId: string, signal?: AbortSignal,
): Promise<MatrixSlice | null> {
  try {
    return await json<MatrixSlice>(`/api/citation/${citationId}/matrix`,
      signal ? { signal } : undefined);
  } catch (e) {
    // A 404 is the expected answer today (route unwritten) and the expected answer
    // forever for a citation with no cells behind it. Both mean "there is no grid
    // here", which is a rendering decision and not an error to report.
    if (e instanceof ApiError && (e.status === 404 || e.status === 501)) return null;
    throw e;
  }
}

/* ------------------------------------------- the lender's own matrix pages */

/**
 * The PDF pages a program's grid was read off, for "View Matrix" on a result card.
 *
 * Henry, 2026-09-14: "Right now it just shows a snippet that AI created. Id like broker to
 * view the full matrix associated with the program that is being quoted." The snippet is
 * MatrixPanel - our extraction of one sub-table. This is the other thing: the lender's own
 * pages, as the lender printed them, which is the only version of the matrix a broker has
 * ever seen before and the only one they can hold our reading against.
 *
 * `pages` and `pageCount` are both needed because a matrix is sometimes one tab of a
 * combined file whose other pages are guideline prose. The viewer opens on the program's
 * own page and lets the broker flip through the rest, rather than pretending the PDF ends
 * where the grid does.
 */
export interface ProgramMatrixDocument {
  versionId: string;
  title: string;
  pageCount: number;
  /** 1-based, ascending: the pages carrying this program's cells. */
  pages: number[];
}

export interface ProgramMatrix {
  programId: string;
  programName: string;
  lenderName: string;
  /** Most relevant first. Empty is a real answer: we hold no matrix page for the program. */
  documents: ProgramMatrixDocument[];
}

export const fetchProgramMatrix = (programId: string, signal?: AbortSignal) =>
  json<ProgramMatrix>(`/api/program/${programId}/matrix`, signal ? { signal } : undefined);

/**
 * One page of a document version, rendered. A URL rather than a fetch because an <img>
 * is what loads it - the cookie rides along same-origin, and the browser's own cache is
 * the right one for a version-scoped page that never changes.
 */
export const documentPageUrl = (versionId: string, page: number): string =>
  `/api/document-version/${versionId}/page/${page}.png?scale=2`;

/* ------------------------------------------------------------ AE contacts */

/**
 * Record that somebody asked for a lender's AE. Fire-and-forget, and deliberately so.
 *
 * This is a metric - "which lenders do brokers actually ring", migration 054 - and it is
 * strictly less important than the thing it measures. The contact is already on screen
 * by the time this runs; a broker who is looking at a phone number must never see an
 * error about our analytics, and must never have the number taken away because a POST
 * failed. So every outcome is swallowed: a network error, a 500, and the 404 that is the
 * expected answer today because `POST /api/ae-contact/click` is still being written on
 * the server. The route landing later needs no change here.
 *
 * Returns void rather than a status for the same reason. There is no caller who should
 * be branching on whether the click was counted.
 */
export async function recordAeContactClick(
  lenderId: string, inquiryId: string | null,
): Promise<void> {
  try {
    await fetch('/api/ae-contact/click', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ lenderId, inquiryId }),
    });
  } catch {
    // Offline, aborted, blocked - all of it is the same non-event to the person reading
    // the contact card.
  }
}

/* -------------------------------------------------------------- contact us */

export interface ContactMessage {
  name: string; phone: string; email: string; message: string;
  /** The honeypot. Always sent empty by ContactUs; a bot that fills it is quietly ignored. */
  website: string;
  /** A solved challenge from fetchContactChallenge - see pow.ts. One solve, one message. */
  pow: { salt: string; challenge: string; signature: string; number: number };
}

/**
 * A proof-of-work challenge for one Contact us message. Fetched when the page opens so the
 * browser can solve it while the broker types. See apps/api/src/contact-pow.ts.
 */
export const fetchContactChallenge = () => json<PowChallenge>('/api/contact/challenge');

/**
 * Send a Contact us message. Unlike the metrics beacons this one DOES throw: a broker who
 * pressed Send needs to know if it did not arrive, and the server only says "received" once
 * the message is stored - see apps/api/src/routes/contact.ts.
 */
export const sendContactMessage = (m: ContactMessage) =>
  json<{ received: true }>('/api/contact', { method: 'POST', body: JSON.stringify(m) });

/* -------------------------------------------------------------------- chat */

export interface ChatReply {
  text: string;
  citationIds: number[];
  references: Reference[];
  toolCalls: string[];
}

export const askFollowUp = (inquiryId: string, question: string, signal?: AbortSignal) =>
  json<ChatReply>(`/api/inquiry/${inquiryId}/message`, {
    method: 'POST',
    body: JSON.stringify({ question }),
    ...(signal ? { signal } : {}),
  });

/* ------------------------------------------------------------------- admin */

export interface AdminLender {
  id: string; slug: string; name: string; active: boolean;
  programs: number; cells: number; documents: number; newest_document: string | null;
}

export interface AdminDocument {
  id: string; kind: string; title: string;
  lender_slug: string; lender_name: string;
  version_id: string | null; status: string | null;
  effective_date: string | null; page_count: number | null;
  activated_at: string | null; ingest_report: unknown;
  age_days: number | null; cells: number; sections: number;
}

export interface AdminInquiry {
  id: string; created_at: string; funky_text: string | null;
  purpose: string | null; loan_amount: string | null; fico: number | null;
  state: string | null; answered: boolean; citations: number;
  username: string | null; thread_messages: number;
}

export interface CorpusHealth {
  counts: { lenders: number; cells: number; sections: number; chunks: number; inquiries: number };
  needsAttention: {
    slug: string; title: string; status: string;
    effective_date: string | null; age_days: number | null;
  }[];
}

export interface ReviewItem {
  id: string; created_at: string; funky_text: string | null;
  purpose: string | null; loan_amount: string | null; property_value: string | null;
  fico: number | null; state: string | null; doc_type: string | null;
  summary: string | null; username: string | null;
}

/** Files nobody could place, that nobody has looked at yet. Unresolved only. */
export const adminReviewQueue = () => json<ReviewItem[]>('/api/admin/review-queue');

/**
 * Contact us messages and what became of each - Admin -> Messages. `held` messages were kept
 * but not emailed (a flood cap, a duplicate, a chatty sender); `failed` ones hit an email
 * error; `sending`/`pending` ones were caught mid-way by a restart. See routes/contact.ts.
 */
export interface AdminContactMessage {
  id: string; createdAt: string; name: string; email: string; phone: string; message: string;
  signedIn: boolean; status: 'emailed' | 'held' | 'failed' | 'sending' | 'pending';
  held: 'duplicate' | 'sender_cap' | 'hourly_cap' | 'daily_cap' | null; emailError: string | null;
}
export const adminContactMessages = () =>
  json<AdminContactMessage[]>('/api/admin/contact-messages');
/** Email one to Henry now, whatever the caps say. */
export const adminSendContactMessage = (id: string) =>
  json<{ sent: boolean; reason?: string }>(`/api/admin/contact-messages/${id}/send`, { method: 'POST' });
export const adminResolveReview = (id: string, note: string) =>
  json<{ ok: true }>(`/api/admin/review-queue/${id}/resolve`, {
    method: 'POST', body: JSON.stringify({ note }),
  });

export const adminHealth = () => json<CorpusHealth>('/api/admin/health');
export const adminLenders = () => json<AdminLender[]>('/api/admin/lenders');
export const adminDocuments = () => json<AdminDocument[]>('/api/admin/documents');
export const adminInquiries = (limit = 50) =>
  json<AdminInquiry[]>(`/api/admin/inquiries?limit=${limit}`);

export const createLender = (body: { slug: string; name: string; active?: boolean }) =>
  json<AdminLender>('/api/admin/lenders', { method: 'POST', body: JSON.stringify(body) });

export const updateLender = (id: string, body: { name?: string; active?: boolean }) =>
  json<AdminLender>(`/api/admin/lenders/${id}`, { method: 'PATCH', body: JSON.stringify(body) });

export const setVersionStatus = (versionId: string, status: 'active' | 'review' | 'superseded') =>
  json<{ id: string; status: string }>(`/api/admin/versions/${versionId}/status`, {
    method: 'POST', body: JSON.stringify({ status }),
  });

/**
 * Deleting a version cascades through its pages, cells, citations and sections, so the
 * server refuses unless the caller echoes back the exact number it is destroying. Called
 * once without a count to learn it, then again to confirm - the two-step is the point,
 * not an inconvenience to design around.
 */
export interface DeletePreview {
  error: 'confirmation_required';
  willDelete: { cells: number; sections: number; total: number };
  title: string;
}

export async function previewVersionDelete(versionId: string): Promise<DeletePreview> {
  const res = await fetch(`/api/admin/versions/${versionId}`, {
    method: 'DELETE', credentials: 'same-origin',
  });
  const body = await res.json().catch(() => ({}));
  if (res.status === 409 && (body as DeletePreview).willDelete) return body as DeletePreview;
  throw new ApiError(res.status, (body as { error?: string }).error ?? 'preview_failed');
}

export const confirmVersionDelete = (versionId: string, total: number) =>
  json<{ deleted: unknown }>(`/api/admin/versions/${versionId}?confirm=${total}`, {
    method: 'DELETE',
  });

/* ----------------------------------------------------------------- metrics */

/**
 * The counts behind "how many View AE Contact clicks did Logan get in September".
 *
 * An /api/admin path, but NOT behind the admin guard: the server gates it on admin OR
 * superuser, because Henry asked for it and is a superuser rather than an admin. The web
 * app asks the same predicate - `canReadMetrics` in @lenderhound/shared - before drawing
 * the nav button, so the button and the 403 cannot disagree.
 */
export interface AeClickMonth { slug: string; name: string; month: string; clicks: number }
export interface AeClickTotal { slug: string; name: string; clicks: number }

/**
 * `eligible`, not "shown", and the difference is real.
 *
 * It counts answers in which the lender came back able to do the loan. The page cards four
 * lenders at a time, and which four is decided at render time and never stored, so a lender
 * can be eligible without having been carded. See routes/metrics.ts - the denominator is a
 * floor, so a click rate computed against it never flatters.
 */
export interface LenderShownMonth { slug: string; name: string; month: string; eligible: number }

export interface Metrics {
  window: { months: number; timezone: string; from: string | null; to: string | null };
  /** Every month in the window, oldest first, including the ones with nothing in them. */
  months: string[];
  aeClicks: { byMonth: AeClickMonth[]; totals: AeClickTotal[] };
  lendersShown: LenderShownMonth[];
  /** `paid` is every inquiry that was not served from the answer cache - see migration 057. */
  searches: { month: string; inquiries: number; cached: number; paid: number }[];
  brokers: { month: string; searchers: number; registered: number }[];
  chat: { month: string; turns: number }[];
  traffic: Traffic;
}

/**
 * Distinct browsers at each step, never page loads - except `visits`, which is there only so
 * a refresh-heavy day can be told apart. See apps/api/src/traffic.ts and migration 083.
 */
export interface FunnelCounts {
  visitors: number; visits: number;
  /** Edited any box; refused by the form on Search at least once (migration 089). */
  formStarted: number; searchBlocked: number;
  searched: number; codeRequested: number; verified: number; answered: number;
}

export interface Traffic {
  /** YYYY-MM-DD of the first recorded step; null until anything is recorded. */
  trackedSince: string | null;
  byMonth: (FunnelCounts & { month: string })[];
  /** Distinct over the whole window: NOT the sum of the months. */
  window: FunnelCounts;
  /** The last fourteen days, oldest first, today last; `campaignVisitors` arrived on a campaign link that day. */
  byDay: (FunnelCounts & { day: string; campaignVisitors: number })[];
  /**
   * The window's funnel per campaign - each visitor under the first campaign link it came in
   * on, `campaign: null` for everyone who never came in on one. Rows sum to `window`.
   */
  byCampaign: (FunnelCounts & { campaign: string | null })[];
  /** The boxes the form refused Search on, each with the browsers it stopped, most first. */
  blockedFields: { field: string; visitors: number }[];
  /** `source` null is direct: typed in, bookmarked, or an email client that sends no referrer. */
  sources: { source: string | null; campaign: string | null; visitors: number; answered: number }[];
}

export const metrics = (months = 6) => json<Metrics>(`/api/admin/metrics?months=${months}`);
